/**
 * Tiny override-merge helpers shared by visual + voice sections.
 *
 * Overrides are persisted server-side as `{ [fieldPath]: { value, editedAt,
 * editedBy } }`. The UI mirrors that map on every chip/cell so a) the chip's
 * "edited" badge can render synchronously and b) the override value can win
 * over the mined value on display.
 */

export function isOverridden(
  userOverrides: Record<string, { value: unknown }> | undefined,
  fieldPath: string
): boolean {
  return !!userOverrides && fieldPath in userOverrides
}

export function readOverride<T>(
  userOverrides: Record<string, { value: unknown }> | undefined,
  fieldPath: string
): T | undefined {
  if (!userOverrides) return undefined
  const entry = userOverrides[fieldPath]
  return entry ? (entry.value as T) : undefined
}

/**
 * Pick the dominant value from a `Record<value, count>` distribution.
 * Returns `null` when the distribution is empty so callers can show "—".
 *
 * Filters against the known enum options so a hallucinated key from the miner
 * doesn't render as raw text.
 */
export function majorityOf<T extends string>(
  distribution: Record<string, number> | undefined,
  options: { value: T; label: string }[]
): T | null {
  if (!distribution) return null
  const entries = Object.entries(distribution)
  if (entries.length === 0) return null
  const [topKey] = entries.reduce((best, current) => (current[1] > best[1] ? current : best))
  return (options.find(o => o.value === topKey)?.value ?? null) as T | null
}
